// src/Views/Projects.js

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ScrambleText from '../Components/ScrambleText';
import ProjectCarousel from '../Components/ProjectCarousel';
import ProjectImageViewer from '../Components/ProjectImageViewer';
import './Projects.css';

import symposiumImage from '../assets/images/Symposium speaking.jpeg';
import NKimage from '../assets/images/NK.png';
import pascalsPrismImage from '../assets/images/PascalsPrism.png';
import telomereImage from '../assets/images/TelomereLab.jpg';
import telomereTeam from '../assets/images/TelomereTeam.jpg';
import telomerePortrait from '../assets/images/TelomerePortrait.jpeg';
import telomereAnalysis from '../assets/images/TelomereAnalysis.jpeg';
import telomereSample from '../assets/images/TelomereSample.jpeg';
import telomereBench from '../assets/images/TelomereBench.jpeg';

/* -------------------------------------------------------------------------- */
/* Project Data                                                               */
/* Add new project cards here by copying the object structure below.           */
/* -------------------------------------------------------------------------- */


const projects = [
  {
    title: 'Telomere Length Quantification',
    organization: 'iCREST Biotechnologies',
    role: 'Research Internship',
    date: 'Summer 2026 · Bangalore, India',
    summary:
      'Quantified absolute telomere length across 50+ blood and cell samples using qPCR. Worked through sample preparation, DNA extraction and assay optimisation, investigating why measurements varied between samples and experimental runs.',
    photos: [
      { src: telomereTeam, alt: 'Group photo at iCREST Biotechnologies.', position: '45% 35%' },
      { src: telomerePortrait, alt: 'Two people posing together at iCREST.', position: '50% 20%' },
      { src: telomereImage, alt: 'Applied Biosystems StepOne instrument and qPCR workstation.', position: '35% 50%' },
      { src: telomereAnalysis, alt: 'StepOne software displaying a qPCR run.', position: '35% 35%' },
      { src: telomereSample, alt: 'A sample tube containing red liquid in the lab.', position: '50% 32%' },
      { src: telomereBench, alt: 'Laboratory workspace with pipettes, sample racks and an open notebook.', position: '50% 28%' },
    ],
    details: (
      <>
        <ul>
          <li>
            PBMC isolation, cell counting and DNA preparation across cord blood,
            adult blood, umbilical cord mesenchymal stem cells, HeLa cells and
            fibroblasts at different passages.
          </li>
          <li>
            Using RayBiotech and ScienCell assays to optimise DNA concentrations,
            controls and qPCR conditions, while investigating sample quality,
            pipetting variation and instrument settings.
          </li>
          <li>
            Analysing qPCR runs in StepOne, with Python supporting calculations
            and technical replicate comparisons.
          </li>
        </ul>
      </>
    ),
  },

  {
    title: 'Cancer Research',
    organization: 'iCrest Academy',
    date: '2022 - 2023',

    summary:
      'Investigated Matrix Metalloproteinase-2 and Tissue Inhibitors of Matrix Metalloproteinase-2 as prognostic markers for head and neck cancer patients. Presented findings at the 2023 iCrest Symposium, highlighting the potential of biomarker ratios in cancer prognosis.',

    paper: 'https://doi.org/10.36838/v6i5.18',
    image: symposiumImage,

    details: (
      <>
        <p>
          For those who don't plan on reading the full research paper, here are
          the key findings:
        </p>

        <ul>
          <li>
            Evaluated plasma biomarkers using ELISA in cancer patients and
            healthy volunteers to investigate cancer progression.
          </li>

          <li>
            Found that the <strong>MMP-2:TIMP-2 ratio</strong> was more than{' '}
            <strong>three times higher</strong> in cancer patients than in
            healthy controls.
          </li>

          <li>
            Demonstrated that biomarker ratios can provide greater prognostic
            value than measuring individual biomarkers alone.
          </li>
        </ul>
      </>
    ),
  },

  {
    title: 'COVID-19 Review Paper',
    date: '2022-2023',

    summary:
      'Conducted an extensive literature review under the mentorship of Dr. Lalita Limaye on the role of Natural Killer (NK) cells in COVID-19. The review examined the relationship between NK cells, cytokine storms, and emerging NK-cell-based therapeutic strategies.',

    paper: 'files/NKcellReviewWeb.pdf',
    image: NKimage,

    details: (
      <>
        <p>
          For those who don't plan on reading the full research paper, here are
          the key findings:
        </p>

        <ul>
          <li>
            Natural Killer (NK) cells play a central role in the body's first line of defence against SARS-CoV-2 by identifying and eliminating infected cells.
          </li>

          <li>
            Severe COVID-19 is associated with a decline in NK-cell numbers, which correlates with an increased risk of cytokine storm and respiratory complications.
          </li>

          <li>
            Strengthening NK-cell function could help regulate excessive immune responses, making NK-cell-based immunotherapy a promising avenue for future COVID-19 treatment.
          </li>
        </ul>
      </>
    ),
  },

  {
    title: 'Chief Technology Officer',
    organization: 'Pascal’s Prism',
    date: '2023',

    summary:
      'A journal created to publish high school students’ articles, reviews, and research papers. As CTO, I developed the website and implemented a content management system to streamline the submission and review process.',

    PascalsPrism: 'https://pascalsprism.wixstudio.com/pascalsprism',
    image: pascalsPrismImage,

    details: (
      <>
        <p>
          I am actually surprised the site still exists 🤣 We stopped working on it ages ago lol 
        </p>

        <p>
          Anyway, we published a few articles and research papers on the site, but it was mostly a fun project to learn about web development and working as a team, almost like being a part of a startup.
        </p>

        <p>
          At some point, academics got real with the IB exams, so we stopped working on it. But I am still proud of what we accomplished and the skills I learned from it.
        </p>
      </>
    ),
  },

];

/* -------------------------------------------------------------------------- */
/* Projects Page Component                                                    */
/* -------------------------------------------------------------------------- */

const Projects = () => {
  const location = useLocation();
  const [openIndex, setOpenIndex] = useState(null);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    const targetIndex = location.state?.fromHomeCard
      ? location.state.openItem
      : null;
    if (!Number.isInteger(targetIndex)) return undefined;

    const timer = window.setTimeout(() => setOpenIndex(targetIndex), 360);
    return () => window.clearTimeout(timer);
  }, [location.state]);

  // Opens a project card. If it is already open, closes it.
  const toggleProject = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <main className="projects-page">
      {/* Page Heading */}
      <section className="projects-hero">
        {/* <div className="projects-eyebrow">
          SELECTED WORK
        </div> */}
        <h1><ScrambleText text="Projects" /></h1>

        <p className="projects-intro">
          Hard work, patience, and perseverance have resulted in the following projects. Click on a project card to learn more about it.
        </p>
      </section>
      
      <section className="projects-list">
        {projects.map((project, index) => {
          const isOpen = openIndex === index;

          return (
            <article
              key={project.title}
              className={`project-card ${isOpen ? 'is-open' : ''}`}
              onClick={() => toggleProject(index)}
            >
              {/* Main visible card content */}
              <div className="project-card__main">
                <div className="project-card__content">
                  <h2><ScrambleText text={project.title} /></h2>

                  {project.organization && (
                    <p className="project-card__organization">
                      {project.role ? `${project.role} · ` : '@'}{project.organization}
                    </p>
                  )}

                  <p className="project-card__date">{project.date}</p>

                  <p className="project-card__summary">
                    {project.summary}
                  </p>

                  <div className="project-card__actions">
                    <button
                      type="button"
                      className="project-card__desktop-cue"
                      aria-expanded={isOpen}
                      aria-controls={`project-details-${index}`}
                      aria-label={`${isOpen ? 'Close' : 'Explore'} ${project.title}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleProject(index);
                      }}
                    >
                      {isOpen ? 'Close project' : 'Explore project'}
                      <span className="project-card__desktop-arrow">
                        {isOpen ? '↑' : '↓'}
                      </span>
                    </button>

                    {project.paper && (
                      <a
                        href={project.paper}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-card__paper-button"
                        onClick={(event) => event.stopPropagation()}
                      >
                        📄 Read Full Paper
                      </a>
                    )}

                    {project.PascalsPrism && (
                      <a
                        href={project.PascalsPrism}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-card__paper-button"
                        onClick={(event) => event.stopPropagation()}
                      >
                        🌐 Visit Pascal’s Prism
                      </a>
                    )}
                  </div>
                </div>

                {project.photos ? (
                  <ProjectCarousel
                    photos={project.photos}
                    title={project.title}
                    suspended={Boolean(viewer)}
                    onOpen={(initialIndex) => setViewer({ photos: project.photos, initialIndex })}
                  />
                ) : (
                  <button
                    type="button"
                    className="project-card__image project-image-button"
                    aria-label={`Enlarge ${project.title} image`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setViewer({ photos: [{ src: project.image, alt: project.imageAlt || project.title }] });
                    }}
                  >
                    <img
                      src={project.image}
                      alt={project.imageAlt || project.title}
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="project-image-expand-cue" aria-hidden="true">⤢</span>
                  </button>
                )}

              </div>

              {/* Hidden details section shown when card is opened */}
              <div
                id={`project-details-${index}`}
                className="project-card__details"
                aria-hidden={!isOpen}
              >
                <div className="project-details">{project.details}</div>
              </div>
            </article>
          );
        })}
      </section>
      {viewer && <ProjectImageViewer {...viewer} onClose={() => setViewer(null)} />}
    </main>
  );
};

export default Projects;
